<template>
  <div class="user-set main-panel large">
    <h1 class="panel-title">版本权限</h1>
    <div class="top">
      <div class="form">
        <div class="row">
          <div class="item col-1">
            <div class="label">功能模块</div>
            <label
              ><input type="text" class="cg-input" v-model="gname"
            /></label>
            <el-button class="publish-btn" size="small" @click="getList"
              >查询</el-button
            >
          </div>
        </div>
      </div>
    </div>
    <el-table
      :data="displayPermissionList"
      border
      height="400px"
      style="width: 100%"
      class="custom-table"
    >
      <el-table-column align="center" label="功能模块" width="300">
        <template #default="scope">
          <div :style="{ paddingLeft: scope.row.level === 1 ? '40px' : '0' }">
            <!-- 主模块加折叠/展开按钮 -->
            <span
              v-if="scope.row.level === 0 && scope.row.hasChildren"
              class="fold-btn"
              @click.stop="toggleFold(scope.row.id)"
            >
              {{ foldStatus[scope.row.id] ? "▼" : "▶" }}
            </span>
            {{ scope.row.content }}
          </div>
        </template>
      </el-table-column>
      <el-table-column align="center" label="注册版">
        <template #default="scope">
          <el-checkbox
            v-model="scope.row.registered"
            @change="(val) => handleCheckChange(val, scope.row.id, 0)"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="商家1号版">
        <template #default="scope">
          <el-checkbox
            v-model="scope.row.business1"
            @change="(val) => handleCheckChange(val, scope.row.id, 1)"
          />
        </template>
      </el-table-column>
      <el-table-column align="center" label="商家2号版">
        <template #default="scope">
          <el-checkbox
            v-model="scope.row.business2"
            @change="(val) => handleCheckChange(val, scope.row.id, 2)"
          />
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, onMounted, getCurrentInstance, watch } from "vue";
const { proxy } = getCurrentInstance();

const gname = ref("");
const permissionList = ref([]); // 原始树形数据
const flatPermissionList = ref([]); // 扁平化后的数据
const foldStatus = ref({}); // 折叠状态：{主模块ID: 是否展开}
const displayPermissionList = ref([]); // 最终显示的列表（控制子模块显隐）
const loading = ref(false);

// 缓存值
const STORAGE_KEY = "version-permission";

// 监听查询条件
watch([gname], () => {
  const searchParams = {
    gname: gname.value,
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(searchParams));
});

// 读取缓存回填表单
const loadSearchCache = () => {
  const cacheStr = sessionStorage.getItem(STORAGE_KEY);
  if (cacheStr) {
    try {
      const cache = JSON.parse(cacheStr);

      gname.value = cache.gname ?? "";
    } catch (e) {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
};

const getList = async () => {
  try {
    loading.value = true;
    const sendata = { content: gname.value };
    const res = await proxy.$api.System.VersionPermission.get(sendata);
    if (res.data.code == "200" && res.data) {
      permissionList.value = formatTreeData(res.data.data.list || []);
      flatPermissionList.value = flattenTree(permissionList.value);
      // 初始化折叠状态（默认全部展开）
      initFoldStatus();
      // 更新显示列表
      updateDisplayList();
    } else {
      proxy.$message.error(res.msg || "查询失败，请重试");
      permissionList.value = [];
      flatPermissionList.value = [];
      displayPermissionList.value = [];
    }
  } catch (error) {
    console.error("查询异常：", error);
    proxy.$message.error("网络错误");
    permissionList.value = [];
    flatPermissionList.value = [];
    displayPermissionList.value = [];
  } finally {
    loading.value = false;
  }
};

// 格式化树形数据（新增hasChildren标记）
const formatTreeData = (rawList) => {
  if (!Array.isArray(rawList) || rawList.length === 0) return [];
  const formatNode = (node, level = 0) => {
    const hasChildren =
      Array.isArray(node.childrenList) && node.childrenList.length > 0;
    const formattedNode = {
      id: node.id,
      pid: node.pid || 0, // 父级ID（主模块pid=0）
      content: node.content,
      level: level,
      hasChildren: hasChildren,
      registered: false,
      business1: false,
      business2: false,
      childrenList: [],
    };
    if (Array.isArray(node.versionList)) {
      node.versionList.forEach((versionItem) => {
        const isChecked = versionItem.delflag === 1;
        switch (versionItem.memberVersion) {
          case 0:
            formattedNode.registered = isChecked;
            break;
          case 1:
            formattedNode.business1 = isChecked;
            break;
          case 2:
            formattedNode.business2 = isChecked;
            break;
          default:
            break;
        }
      });
    }
    if (hasChildren) {
      formattedNode.childrenList = node.childrenList.map((child) =>
        formatNode(child, 1)
      );
    }
    return formattedNode;
  };
  return rawList.map((node) => formatNode(node));
};

// 扁平化树形数据
const flattenTree = (tree) => {
  const result = [];
  const flatten = (nodes) => {
    nodes.forEach((node) => {
      result.push(node);
      if (node.childrenList && node.childrenList.length > 0) {
        flatten(node.childrenList);
      }
    });
  };
  flatten(tree);
  return result;
};

// 初始化折叠状态（默认全部展开）
const initFoldStatus = () => {
  foldStatus.value = {};
  // 遍历所有主模块，标记为展开状态
  flatPermissionList.value.forEach((item) => {
    if (item.level === 0 && item.hasChildren) {
      foldStatus.value[item.id] = true; // true=展开，false=折叠
    }
  });
};

// 切换折叠/展开状态
const toggleFold = (mainId) => {
  foldStatus.value[mainId] = !foldStatus.value[mainId];
  updateDisplayList();
};

// 更新显示列表（控制子模块显隐）
const updateDisplayList = () => {
  displayPermissionList.value = [];
  flatPermissionList.value.forEach((item) => {
    // 主模块始终显示
    if (item.level === 0) {
      displayPermissionList.value.push(item);
    }
    // 子模块：仅当父模块展开时显示
    else if (item.level === 1 && foldStatus.value[item.pid]) {
      displayPermissionList.value.push(item);
    }
  });
};

// 权限修改逻辑（不变）
const handleCheckChange = async (val, moduleId, memberVersion) => {
  const sendata = {
    memberMenuId: moduleId,
    memberVersion: memberVersion,
    delflag: val ? 1 : 0,
  };
  try {
    const res = await proxy.$api.System.VersionPermission.update(sendata);
    if (res.data?.code === "200") {
      proxy.$message.success("权限修改成功");
    } else {
      rollbackCheckStatus(moduleId, memberVersion, !val);
      proxy.$message.error(res.msg || "权限修改失败");
    }
  } catch (error) {
    rollbackCheckStatus(moduleId, memberVersion, !val);
    console.error("修改权限异常：", error);
    proxy.$message.error("网络错误，修改失败");
  }
};

// 回滚勾选状态（适配displayPermissionList）
const rollbackCheckStatus = (moduleId, memberVersion, originalVal) => {
  // 先找扁平化列表（避免显示列表过滤导致找不到）
  const targetRow = flatPermissionList.value.find(
    (item) => item.id === moduleId
  );
  if (!targetRow) return;
  switch (memberVersion) {
    case 0:
      targetRow.registered = originalVal;
      break;
    case 1:
      targetRow.business1 = originalVal;
      break;
    case 2:
      targetRow.business2 = originalVal;
      break;
  }
  // 同步到显示列表
  const displayRow = displayPermissionList.value.find(
    (item) => item.id === moduleId
  );
  if (displayRow) {
    switch (memberVersion) {
      case 0:
        displayRow.registered = originalVal;
        break;
      case 1:
        displayRow.business1 = originalVal;
        break;
      case 2:
        displayRow.business2 = originalVal;
        break;
    }
  }
};

onMounted(() => {
  loadSearchCache();
  getList();
});
</script>

<style lang="scss" scoped>
// 完全保留你原有样式，仅新增折叠按钮样式
@import "./version-permission.scss";
.panel-title {
  color: #999;
}
.fold-btn {
  display: inline-block;
  width: 20px;
  text-align: center;
  cursor: pointer;
  user-select: none;
  margin-right: 5px;
  font-weight: bold;
  color: #666;
}

.publish-btn {
  background-color: #ff9900;
}
</style>
